import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Cross, ShieldCheck, HeartHandshake, Search, ArrowRight, ArrowLeft, CalendarPlus,
  Phone, Copy, Video, Clock, Check, ExternalLink, MapPin, Stethoscope, Box, AlertTriangle,
} from "lucide-react";

const NEEDS = [
  { id: "specialiste", label: "Téléconsultation spécialiste" },
  { id: "compte_partenaire", label: "J'ai déjà un compte sur la plateforme" },
];

function openExternal(url) {
  try {
    const u = new URL(url);
    if (u.protocol === "https:" && (u.hostname.endsWith("doctolib.fr") || u.hostname.endsWith("qare.fr"))) {
      window.open(url, "_blank", "noopener,noreferrer"); return true;
    }
  } catch (e) {}
  return false;
}

const DOCTOLIB_LOCATION_SLUG = "la-londe-les-maures";

const dt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }) : "—";
const hm = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }) : "—";
const hmFr = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }).replace(":", "h") : "—";
const dayKey = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Paris" });

const RDV_CONSENT = "J'accepte que mes données personnelles (nom, prénom) soient traitées par la pharmacie uniquement pour gérer et confirmer mon rendez-vous. Conservation limitée, aucun partage à des fins commerciales. Je peux exercer mes droits (accès, rectification, effacement) auprès de la pharmacie.";

function Shell({ children, step, total = 8 }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur-xl">
        <div className="mx-auto max-w-2xl px-4 h-16 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground"><Cross className="h-5 w-5" /></div>
          <div><div className="font-heading font-bold leading-tight">Téléconsultation</div><div className="text-[11px] text-muted-foreground">Pharmacie des Écoles</div></div>
        </div>
        {step > 0 && <div className="h-1 bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${(step / total) * 100}%` }} /></div>}
      </header>
      <main className="flex-1 mx-auto max-w-2xl w-full px-4 py-8">{children}</main>
      <footer className="border-t bg-muted/30 text-[11px] text-muted-foreground px-4 py-4 text-center space-y-1">
        <p>La cabine est réservée selon l'heure de votre rendez-vous médical. Elle ne garantit pas l'accès à un spécialiste.</p>
        <p className="font-semibold text-destructive">En cas d'urgence, contactez le 15 ou le 112.</p>
      </footer>
    </div>
  );
}

export default function PatientFlow() {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("accueil");
  const [req, setReq] = useState(null);
  const [need, setNeed] = useState(null);
  const [specSlug, setSpecSlug] = useState(null);
  const [q, setQ] = useState("");
  const [prevention, setPrevention] = useState({ accompanimentRequested: false });
  const [partnerUsed, setPartnerUsed] = useState(null);
  const [partnerUrl, setPartnerUrl] = useState(null);
  const [appt, setAppt] = useState({ date: "", time: "", durationMinutes: 30, platform: "" });
  const [cabinResult, setCabinResult] = useState(null); // {cabins, anyAvailable, appointmentStart, durationMinutes}
  const [heldCabin, setHeldCabin] = useState(null);      // {cabinName, arrivalAt, endAt}
  const [held, setHeld] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [info, setInfo] = useState({ patientFirstName: "", patientLastName: "" });
  const [consentRdv, setConsentRdv] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookup, setLookup] = useState({ code: "" });
  const [detail, setDetail] = useState(null);
  const [book, setBook] = useState({ date: "", time: "", doctorName: "" });
  const [assignment, setAssignment] = useState(null); // {cabinName, startAt, doctorName}
  const dateRef = useRef(null);
  const timeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/public/config").then((r) => { setConfig(r.data); setAppt((a) => ({ ...a, durationMinutes: r.data.defaultDurationMinutes || 30 })); }); }, []);
  useEffect(() => {
    if (!held) return;
    const t = setInterval(() => {
      const s = Math.max(0, Math.round((new Date(held).getTime() - Date.now()) / 1000));
      setCountdown(s); if (s <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [held]);

  const specs = config?.specialites || [];
  const partners = config?.partners || [];
  const durationOptions = config?.durationOptions || [15, 20, 30, 45, 60];
  const specName = useMemo(() => specs.find((s) => s.slug === specSlug)?.name, [specs, specSlug]);
  const filtered = useMemo(() => {
    const n = q.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    return specs.filter((s) => s.name.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").includes(n));
  }, [q, specs]);
  const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

  const startFlow = async (n) => {
    setNeed(n);
    try {
      const { data } = await api.post("/public/requests", { requestType: n.id });
      if (n.spec) api.post("/public/stats/specialty", { specialtySlug: n.spec, pharmacyId: config?.pharmacyId || null }).catch(() => {});
      setReq(data);
      if (n.spec) { setSpecSlug(n.spec); setStep("bilan_offer"); }
      else setStep("specialite");
    } catch (e) { err(e); }
  };

  const chooseSpec = async () => {
    if (!specSlug) { toast.error("Choisissez une spécialité."); return; }
    api.post("/public/stats/specialty", { specialtySlug: specSlug, pharmacyId: config?.pharmacyId || null }).catch(() => {});
    const spec = specs.find((s) => s.slug === specSlug);
    if (spec?.preventionMode === "masque") setStep("orientation");
    else setStep("bilan_offer");
  };

  const saveBilan = async () => {
    try { await api.post(`/public/requests/${req.id}/prevention`, prevention); setStep("orientation"); }
    catch (e) { err(e); }
  };

  // Construit l'URL partenaire côté client pour ouvrir l'onglet SYNCHRONE au clic.
  // Doctolib : page nationale de la spécialité (sans ville) + filtre vidéo => téléconsultation partout en France.
  const buildPartnerUrl = (slug) => {
    const p = partners.find((x) => x.slug === slug);
    const spec = specs.find((s) => s.slug === specSlug);
    if (slug === "doctolib") {
      const docSlug = spec?.partnerMappings?.doctolib || "medecin-generaliste";
      return `https://www.doctolib.fr/${docSlug}/france?telehealth=true`;
    }
    const qareMap = spec?.partnerMappings?.qare;
    return qareMap || p?.baseUrl || "https://www.qare.fr/recherche-praticien?localisation=all&page=1&resultatsParPage=10";
  };

  const goPartner = (slug) => {
    const url = buildPartnerUrl(slug);
    openExternal(url);
    setPartnerUrl(url); setPartnerUsed(slug); setAppt((a) => ({ ...a, platform: slug }));
    api.post(`/public/requests/${req.id}/redirect?partnerSlug=${slug}&locationSlug=${DOCTOLIB_LOCATION_SLUG}`).catch(() => {});
    setStep("retour");
  };

  const declineMedical = async () => {
    try { await api.post(`/public/requests/${req.id}/no-medical`); } catch (e) {}
    setStep("no_dispo");
  };

  const openBookForm = () => {
    // Pré-remplit l'heure avec l'instant présent (Europe/Paris) ; le patient ajuste.
    const now = new Date();
    const date = now.toLocaleDateString("sv-SE", { timeZone: "Europe/Paris" });
    const time = now.toLocaleTimeString("en-GB", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit", hour12: false });
    setBook({ date, time, doctorName: "" });
    setStep("book_form");
  };

  const autoBook = async () => {
    const time = (timeRef.current?.value || book.time || "").trim();
    if (!time) { toast.error("Indiquez l'heure de votre rendez-vous."); return; }
    if (!info.patientFirstName.trim() || !info.patientLastName.trim()) {
      toast.error("Renseignez votre prénom et votre nom."); return;
    }
    if (!consentRdv) { toast.error("Merci d'accepter le traitement de vos données."); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/public/requests/${req.id}/auto-book`, {
        time, date: book.date, doctorName: book.doctorName || null,
        patientFirstName: info.patientFirstName, patientLastName: info.patientLastName });
      if (!data.assigned) { setAssignment(null); setStep("no_cabin_free"); return; }
      setAssignment(data);
      const { data: d } = await api.get(`/public/requests/${req.id}`); setDetail(d);
      setStep("confirmation");
    } catch (e) { err(e); } finally { setLoading(false); }
  };

  const doLookup = async () => {
    try { const { data } = await api.post("/public/requests/lookup", lookup); setDetail(data); setStep("existing_result"); }
    catch (e) { err(e); }
  };

  const arrival = async () => {
    await api.post(`/public/requests/${detail.id}/arrival`);
    toast.success("Arrivée confirmée. Présentez-vous au comptoir.");
    const { data } = await api.get(`/public/requests/${detail.id}`); setDetail(data);
  };

  const smsText = detail && detail.booking
    ? `Téléconsultation ${specName || ""} le ${dt(detail.partnerAppointmentStart)}. Présentez-vous dès ${hm(detail.booking.startAt)} à la Pharmacie des Écoles avec votre carte Vitale.`
    : "";

  if (!config) return <Shell step={0}><p className="text-muted-foreground">Chargement…</p></Shell>;

  if (step === "accueil") return (
    <Shell step={0}>
      <BackBtn onClick={() => navigate(-1)} />
      <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 px-3 py-1 text-sm font-semibold mb-4"><MapPin className="h-4 w-4" /> {config.pharmacie.city}</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Téléconsultation dans votre pharmacie</h1>
      <p className="text-muted-foreground text-lg mb-6">Trouvez d'abord votre rendez-vous médical, puis réservez l'espace de téléconsultation.</p>
      <p className="text-sm text-muted-foreground mb-8">Réalisez votre téléconsultation dans un espace dédié, confidentiel et sécurisé. Selon votre situation, un bilan prévention peut vous être proposé.</p>
      <Button data-testid="start-btn" onClick={() => startFlow(NEEDS[0])} className="w-full h-16 rounded-2xl text-lg font-semibold gap-2 mb-3">Commencer <ArrowRight className="h-5 w-5" /></Button>
      <Button variant="outline" onClick={() => setStep("existing")} data-testid="existing-btn" className="w-full h-14 rounded-2xl">J'ai déjà un rendez-vous</Button>
      <div className="grid grid-cols-3 gap-3 mt-10 text-center text-xs text-muted-foreground">
        <div><ShieldCheck className="h-6 w-6 mx-auto mb-1 text-primary" />Confidentiel</div>
        <div><HeartHandshake className="h-6 w-6 mx-auto mb-1 text-primary" />Accompagné</div>
        <div><Cross className="h-6 w-6 mx-auto mb-1 text-primary" />En pharmacie</div>
      </div>
    </Shell>
  );

  if (step === "specialite") return (
    <Shell step={2}>
      <BackBtn onClick={() => setStep("accueil")} />
      <h1 className="font-heading text-3xl font-bold mb-4">Choisissez une spécialité</h1>
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input data-testid="spec-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une spécialité" className="h-14 pl-12 rounded-2xl border-2" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-6">
        {filtered.map((s) => (
          <button key={s.slug} data-testid={`spec-${s.slug}`} onClick={() => setSpecSlug(s.slug)}
            className={`rounded-xl border-2 p-3 text-sm text-left ${specSlug === s.slug ? "border-primary bg-primary/5 text-primary font-semibold" : "hover:border-primary/50"}`}>{s.name}</button>
        ))}
      </div>
      <Button data-testid="see-avail-btn" onClick={chooseSpec} disabled={!specSlug} className="w-full h-14 rounded-2xl gap-2">Continuer <ArrowRight className="h-5 w-5" /></Button>
    </Shell>
  );

  if (step === "bilan_offer") return (
    <Shell step={3}>
      <h1 className="font-heading text-3xl font-bold mb-2">Bilan prévention</h1>
      <p className="text-muted-foreground mb-8">Avant de poursuivre, la réalisation du bilan prévention est nécessaire pour préparer au mieux votre prise en charge.</p>
      <Button data-testid="do-bilan" onClick={() => navigate("/prevention")} className="w-full h-14 rounded-2xl">Réaliser le bilan prévention</Button>
    </Shell>
  );

  if (step === "bilan_q") {
    const YN = ({ id, label }) => (
      <div className="flex items-center justify-between rounded-xl border p-3">
        <span className="text-sm">{label}</span>
        <div className="flex gap-2">
          {[["Oui", true], ["Non", false]].map(([t, v]) => (
            <button key={t} onClick={() => setPrevention((p) => ({ ...p, [id]: v }))}
              className={`px-4 h-9 rounded-lg text-sm ${prevention[id] === v ? "bg-primary text-primary-foreground" : "border"}`}>{t}</button>
          ))}
        </div>
      </div>
    );
    return (
      <Shell step={4}>
        <h1 className="font-heading text-3xl font-bold mb-4">Bilan prévention</h1>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between rounded-xl border p-3"><span className="text-sm">Votre tranche d'âge</span>
            <select data-testid="bilan-age" value={prevention.ageRange || ""} onChange={(e) => setPrevention((p) => ({ ...p, ageRange: e.target.value }))} className="h-9 rounded-lg border px-2 text-sm bg-background">
              <option value="">—</option>{["<18", "18-39", "40-59", "60-74", "75+"].map((a) => <option key={a}>{a}</option>)}</select></div>
          <YN id="hasPrimaryDoctor" label="Avez-vous un médecin traitant ?" />
          <YN id="hypertension" label="Avez-vous de l'hypertension ?" />
          <YN id="diabetes" label="Avez-vous du diabète ?" />
          <YN id="smoking" label="Fumez-vous actuellement ?" />
          <YN id="stress" label="Vous sentez-vous régulièrement stressé ?" />
          <YN id="weightDiscussion" label="Souhaitez-vous aborder la question du poids ?" />
          <YN id="accompanimentRequested" label="Souhaitez-vous être accompagné par l'équipe ?" />
        </div>
        <p className="text-xs text-muted-foreground my-4">J'accepte que ces réponses soient utilisées uniquement pour organiser mon parcours dans la pharmacie.</p>
        <Button data-testid="validate-bilan" onClick={saveBilan} className="w-full h-14 rounded-2xl">Valider mon bilan</Button>
      </Shell>
    );
  }

  if (step === "orientation") return (
    <Shell step={5}>
      <h1 className="font-heading text-3xl font-bold mb-4">Votre orientation</h1>
      <Card className="rounded-2xl p-5 mb-4 bg-primary/5 border-primary/20">
        <h3 className="font-heading font-semibold mb-3">Téléconsultation adaptée à votre besoin</h3>
        <ul className="text-sm space-y-1.5">
          <li>• Spécialité : <b>{specName || "Médecine générale"}</b></li>
          <li>• Bilan prévention : <b>{prevention.ageRange || prevention.hasPrimaryDoctor !== undefined ? "fait" : "non fait"}</b></li>
        </ul>
      </Card>
      <p className="text-xs text-muted-foreground mb-6">Cette orientation est indicative et ne constitue pas un diagnostic.</p>
      <Button data-testid="goto-search-btn" onClick={() => setStep("recherche_medicale")} className="w-full h-14 rounded-2xl gap-2">Rechercher un rendez-vous médical <ArrowRight className="h-5 w-5" /></Button>
    </Shell>
  );

  if (step === "recherche_medicale") return (
    <Shell step={6}>
      <div className="h-14 w-14 rounded-2xl bg-secondary/10 grid place-items-center text-secondary mb-4"><Stethoscope className="h-7 w-7" /></div>
      <h1 className="font-heading text-3xl font-bold mb-2">Trouvez d'abord votre rendez-vous médical</h1>
      <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary/10 text-secondary border border-secondary/30 px-3 py-1 text-sm font-semibold mb-3"><MapPin className="h-4 w-4" /> Téléconsultation partout en France · {specName || "Médecine générale"}</div>
      <p className="text-sm text-muted-foreground mb-6">Vous allez consulter les disponibilités réelles sur la plateforme de rendez-vous. Après avoir sélectionné votre rendez-vous, revenez ici pour réserver l'espace de téléconsultation de la pharmacie.</p>
      <div className="space-y-3">
        {partners.map((p) => (
          <Button key={p.slug} data-testid={`partner-${p.slug}`} disabled={loading} onClick={() => goPartner(p.slug)} className="w-full h-16 rounded-2xl gap-2 text-base">
            <Video className="h-5 w-5" /> Rechercher sur {p.name} <ExternalLink className="h-4 w-4" />
          </Button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4">Cette application n'est pas affiliée à ces plateformes : elle assure uniquement une redirection. Aucune donnée médicale n'est transmise.</p>
    </Shell>
  );

  if (step === "retour") return (
    <Shell step={6}>
      <h1 className="font-heading text-3xl font-bold mb-2">Avez-vous trouvé un rendez-vous ?</h1>
      <p className="text-sm text-muted-foreground mb-4">La pharmacie ne reçoit pas automatiquement la confirmation de la plateforme de rendez-vous.</p>
      {partnerUrl && <Button variant="outline" onClick={() => openExternal(partnerUrl)} className="w-full h-12 rounded-2xl mb-5 gap-2"><ExternalLink className="h-4 w-4" /> Rouvrir la plateforme</Button>}
      <div className="space-y-3">
        <Button data-testid="found-yes" onClick={openBookForm} disabled={loading} className="w-full h-14 rounded-2xl gap-2"><Check className="h-5 w-5" /> Oui, j'ai réservé un rendez-vous</Button>
        <Button data-testid="found-no" variant="outline" onClick={declineMedical} className="w-full h-14 rounded-2xl">Non, aucune disponibilité ne me convient</Button>
        <Button data-testid="found-later" variant="ghost" onClick={() => { toast.info(`Reprenez plus tard avec votre code ${req?.publicCode}.`); setStep("accueil"); }} className="w-full h-12 rounded-2xl">Je continuerai plus tard</Button>
      </div>
    </Shell>
  );

  if (step === "no_dispo") return (
    <Shell step={6}>
      <div className="h-14 w-14 rounded-2xl bg-amber-500/10 grid place-items-center text-amber-600 mb-4"><AlertTriangle className="h-7 w-7" /></div>
      <h1 className="font-heading text-3xl font-bold mb-2">Aucune disponibilité pour l'instant</h1>
      <p className="text-sm text-muted-foreground mb-6">Vous pouvez essayer une autre plateforme, élargir votre recherche ou nous contacter.</p>
      <div className="space-y-3">
        <Button data-testid="other-platform" onClick={() => setStep("recherche_medicale")} className="w-full h-14 rounded-2xl">Rechercher sur une autre plateforme</Button>
        <Button data-testid="back-spec" variant="outline" onClick={() => setStep("specialite")} className="w-full h-14 rounded-2xl">Revenir au choix de la spécialité</Button>
        <a href={`tel:${config.pharmacie.phone.replace(/\s/g, "")}`}><Button variant="outline" className="w-full h-14 rounded-2xl gap-2"><Phone className="h-4 w-4" /> Contacter la pharmacie</Button></a>
      </div>
    </Shell>
  );

  if (step === "book_form") {
    const canSubmit = !!book.time && info.patientFirstName.trim() && info.patientLastName.trim() && consentRdv;
    return (
    <Shell step={7}>
      <BackBtn onClick={() => setStep("retour")} />
      <h1 className="font-heading text-3xl font-bold mb-1">Votre rendez-vous</h1>
      <p className="text-sm text-muted-foreground mb-6">Indiquez la date et l'heure de votre rendez-vous médical et vos coordonnées. Nous vous attribuons automatiquement une cabine disponible à cette heure.</p>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-sm">Date du rendez-vous</Label>
            <Input ref={dateRef} type="date" data-testid="book-date" value={book.date} onChange={(e) => setBook({ ...book, date: e.target.value })} className="h-12 rounded-xl" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Heure du rendez-vous</Label>
            <Input ref={timeRef} type="time" data-testid="book-time" value={book.time} onChange={(e) => setBook({ ...book, time: e.target.value })} className="h-12 rounded-xl" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-sm">Prénom</Label>
            <Input data-testid="book-firstname" value={info.patientFirstName} onChange={(e) => setInfo({ ...info, patientFirstName: e.target.value })} className="h-12 rounded-xl" /></div>
          <div className="space-y-1.5"><Label className="text-sm">Nom</Label>
            <Input data-testid="book-lastname" value={info.patientLastName} onChange={(e) => setInfo({ ...info, patientLastName: e.target.value })} className="h-12 rounded-xl" /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-sm">Médecin <span className="text-muted-foreground">(facultatif)</span></Label>
          <Input data-testid="book-doctor" placeholder="Dr ..." value={book.doctorName} onChange={(e) => setBook({ ...book, doctorName: e.target.value })} className="h-12 rounded-xl" /></div>
        <div className="rounded-xl bg-muted/50 p-3 text-sm">Spécialité : <b>{specName || "Médecine générale"}</b></div>
      </div>
      <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="book-consent">
        <input type="checkbox" checked={consentRdv} onChange={(e) => setConsentRdv(e.target.checked)} className="mt-0.5 h-5 w-5 rounded border-2 accent-primary shrink-0" data-testid="book-consent-checkbox" />
        <span className="text-xs text-muted-foreground leading-relaxed">{RDV_CONSENT}</span>
      </label>
      <Button data-testid="assign-cabin-btn" onClick={autoBook} disabled={loading || !canSubmit} className="w-full h-14 rounded-2xl gap-2 mt-6">Continuer <ArrowRight className="h-5 w-5" /></Button>
    </Shell>
  );
  }

  if (step === "no_cabin_free") return (
    <Shell step={7}>
      <BackBtn onClick={() => setStep("book_form")} />
      <div className="h-14 w-14 rounded-2xl bg-rose-500/10 grid place-items-center text-rose-600 mb-4"><AlertTriangle className="h-7 w-7" /></div>
      <h1 className="font-heading text-3xl font-bold mb-2">Aucune cabine disponible à cette heure</h1>
      <p className="text-sm text-muted-foreground mb-6">Nos deux cabines sont déjà réservées à l'heure indiquée. Essayez une autre heure ou contactez la pharmacie.</p>
      <div className="space-y-3">
        <Button data-testid="try-other-time" onClick={() => setStep("book_form")} className="w-full h-14 rounded-2xl">Choisir une autre heure</Button>
        <a href={`tel:${config.pharmacie.phone.replace(/\s/g, "")}`}><Button variant="outline" className="w-full h-14 rounded-2xl gap-2"><Phone className="h-4 w-4" /> Contacter la pharmacie</Button></a>
      </div>
    </Shell>
  );

  if (step === "confirmation") return (
    <Shell step={8}>
      <div className="text-center mb-6"><div className="h-16 w-16 rounded-full bg-primary/10 grid place-items-center mx-auto mb-3"><Check className="h-8 w-8 text-primary" /></div>
        <h1 className="font-heading text-3xl font-bold">Réservation confirmée</h1></div>
      <Card className="rounded-2xl p-5 mb-4 bg-primary/5 border-primary/30" data-testid="cabin-assignment">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center text-primary-foreground shrink-0"><Box className="h-5 w-5" /></div>
          <div className="space-y-1.5">
            {(assignment?.isTest || detail?.isTest) && <span data-testid="confirm-test-badge" className="inline-block text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 rounded-full px-2 py-0.5">Mode test</span>}
            <p className="text-base leading-relaxed">Votre rendez-vous de <b>{hmFr(assignment?.startAt || detail?.partnerAppointmentStart)}</b>{(assignment?.doctorName || detail?.doctorName) ? <> avec le <b>Dr {assignment?.doctorName || detail?.doctorName}</b></> : null} se déroulera au sein de la <b className="text-primary">{assignment?.cabinName || detail?.cabinName}</b>, à la même heure.</p>
          </div>
        </div>
      </Card>
      <Card className="rounded-2xl p-5 mb-4 space-y-1.5 text-sm">
        <p>Spécialité : <b>{specName || "—"}</b></p>
        <p>Rendez-vous : <b>{dt(detail?.partnerAppointmentStart)}</b></p>
        <p>Cabine attribuée : <b>{assignment?.cabinName || detail?.cabinName || "—"}</b></p>
        <p>Patient : <b>{detail?.patientFirstName} {detail?.patientLastName}</b></p>
        <p>Pharmacie : {config.pharmacie.phone}</p>
      </Card>
      <p className="text-sm text-muted-foreground mb-4">Présentez-vous à l'heure indiquée avec votre carte Vitale et tout document utile.</p>
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="add-calendar" variant="outline" onClick={() => downloadIcs(detail, config)} className="h-12 rounded-xl gap-1.5"><CalendarPlus className="h-4 w-4" /> Calendrier</Button>
        <a href={`tel:${config.pharmacie.phone.replace(/\s/g, "")}`}><Button variant="outline" className="w-full h-12 rounded-xl gap-1.5"><Phone className="h-4 w-4" /> Appeler</Button></a>
        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${specName || ""} — RDV ${dt(detail?.partnerAppointmentStart)} — Arrivée ${detail?.booking ? hm(detail.booking.startAt) : ""} — ${config.pharmacie.address}`); toast.success("Récapitulatif copié"); }} className="h-12 rounded-xl gap-1.5"><Copy className="h-4 w-4" /> Copier</Button>
        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(smsText); toast.success("Texte de rappel SMS copié"); }} className="h-12 rounded-xl gap-1.5"><Phone className="h-4 w-4" /> Rappel SMS</Button>
      </div>
      <Button data-testid="goto-arrival" onClick={() => setStep("existing_result")} className="w-full h-12 rounded-2xl mt-4">J'y suis — confirmer mon arrivée</Button>
    </Shell>
  );

  if (step === "existing") return (
    <Shell step={0}>
      <BackBtn onClick={() => setStep("accueil")} />
      <h1 className="font-heading text-3xl font-bold mb-4">J'ai déjà un rendez-vous</h1>
      <div className="space-y-3">
        <Input data-testid="lookup-code" placeholder="Code de demande (PH-XXXXXX)" value={lookup.code} onChange={(e) => setLookup({ ...lookup, code: e.target.value })} className="h-12 rounded-xl" />
      </div>
      <Button data-testid="lookup-btn" onClick={doLookup} className="w-full h-14 rounded-2xl mt-4">Rechercher</Button>
    </Shell>
  );

  if (step === "existing_result") return (
    <Shell step={0}>
      <h1 className="font-heading text-3xl font-bold mb-4">Votre rendez-vous</h1>
      <Card className="rounded-2xl p-5 mb-4 space-y-1.5 text-sm">
        <p>Spécialité : <b>{specName || "—"}</b></p>
        <p>Rendez-vous médical : <b>{dt(detail?.partnerAppointmentStart)}</b></p>
        <p>Arrivée en pharmacie : <b>{detail?.booking ? dt(detail.booking.startAt) : "—"}</b></p>
        <p>Statut : <b>{detail?.status}</b></p>
      </Card>
      {detail?.status !== "PATIENT_ARRIVE" ? (
        <Button data-testid="confirm-arrival" onClick={arrival} className="w-full h-14 rounded-2xl">Je confirme ma venue</Button>
      ) : <p className="text-primary font-semibold text-center">Arrivée confirmée ✓ Présentez-vous au comptoir.</p>}
      <Button variant="ghost" onClick={() => setStep("accueil")} className="w-full h-12 rounded-2xl mt-2">Retour à l'accueil</Button>
    </Shell>
  );

  return <Shell step={0}><p>—</p></Shell>;
}

function BackBtn({ onClick }) {
  return <button onClick={onClick} className="text-sm text-muted-foreground flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Retour</button>;
}

function downloadIcs(detail, config) {
  if (!detail?.booking) { toast.error("Aucune réservation."); return; }
  const s = new Date(detail.booking.startAt), e = new Date(detail.booking.endAt);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//PharmaRDV//FR\nBEGIN:VEVENT\nUID:${detail.id}@pharma\nDTSTAMP:${fmt(new Date())}\nDTSTART:${fmt(s)}\nDTEND:${fmt(e)}\nSUMMARY:Téléconsultation ${detail.specialtyName || ""} - Pharmacie des Écoles\nLOCATION:${config.pharmacie.address}, ${config.pharmacie.city}\nEND:VEVENT\nEND:VCALENDAR`;
  const blob = new Blob([ics], { type: "text/calendar" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "teleconsultation.ics"; a.click();
}
